import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { usePreviewMode } from "./PreviewModeContext";
import { renderRichText, markupToHtml, htmlToMarkup, rgbToHex } from "./richText";
import "./edit.css";

// Mirrors ALLOWED_STYLE_KEYS in server/contentApiRoutes.js
const STYLE_FIELDS = [
  { key: "color", label: "颜色", kind: "color" },
  { key: "fontSize", label: "字号", kind: "text", placeholder: "如 18px" },
  {
    key: "fontWeight",
    label: "粗细",
    kind: "select",
    options: ["", "normal", "500", "600", "700", "bold"],
  },
  {
    key: "textAlign",
    label: "对齐",
    kind: "select",
    options: ["", "left", "center", "right"],
  },
  { key: "lineHeight", label: "行高", kind: "text", placeholder: "如 1.6" },
  { key: "marginTop", label: "上边距", kind: "text", placeholder: "如 40px" },
  { key: "marginBottom", label: "下边距", kind: "text" },
  { key: "marginLeft", label: "左边距", kind: "text" },
  { key: "marginRight", label: "右边距", kind: "text" },
  { key: "paddingTop", label: "上内距", kind: "text" },
  { key: "paddingBottom", label: "下内距", kind: "text" },
  { key: "paddingLeft", label: "左内距", kind: "text" },
  { key: "paddingRight", label: "右内距", kind: "text" },
];

function toCssString(value) {
  if (value === undefined || value === null || value === "") return "";
  return String(value);
}

function toHexColor(value) {
  return rgbToHex(value);
}

function stylesEqual(a, b) {
  const left = a || {};
  const right = b || {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (toCssString(left[key]) !== toCssString(right[key])) return false;
  }
  return true;
}

function isHttpOrMailto(href) {
  return /^(https?:\/\/|mailto:)/i.test(String(href || "").trim());
}

function WysiwygEditor({ value, style, onChange, onSelectionColor, onActiveLink, applyColorRef, applyLinkRef }) {
  const editorRef = useRef(null);
  const savedRange = useRef(null);
  const lastMarkup = useRef(null);
  const skipSync = useRef(false);
  const onChangeRef = useRef(onChange);
  const onSelectionColorRef = useRef(onSelectionColor);
  const onActiveLinkRef = useRef(onActiveLink);
  onChangeRef.current = onChange;
  onSelectionColorRef.current = onSelectionColor;
  onActiveLinkRef.current = onActiveLink;

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    if (lastMarkup.current !== value) {
      el.innerHTML = markupToHtml(value);
      lastMarkup.current = value;
    }
  }, [value]);

  function emitFromDom() {
    const el = editorRef.current;
    if (!el) return;
    const markup = htmlToMarkup(el);
    skipSync.current = true;
    lastMarkup.current = markup;
    onChangeRef.current(markup);
  }

  function rememberRange() {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!sel.rangeCount || !el) return;
    const node = sel.anchorNode;
    if (!node || (!el.contains(node) && node !== el)) return;
    savedRange.current = sel.getRangeAt(0).cloneRange();
    let probe = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    if (!probe || !el.contains(probe)) probe = el;
    const hex = rgbToHex(window.getComputedStyle(probe).color);
    onSelectionColorRef.current(hex, !sel.getRangeAt(0).collapsed);
    const anchor = probe && probe.closest ? probe.closest("a") : null;
    if (anchor && el.contains(anchor) && onActiveLinkRef.current) {
      onActiveLinkRef.current({
        href: anchor.getAttribute("href") || "",
        text: (anchor.innerText || "").replace(/\s+/g, " ").trim(),
      });
    } else if (onActiveLinkRef.current) {
      onActiveLinkRef.current(null);
    }
  }

  function applyColor(hex) {
    const el = editorRef.current;
    if (!el || !savedRange.current) return false;
    el.focus();
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange.current);
    if (sel.isCollapsed) return false;
    const range = savedRange.current;
    const hitAnchors = Array.from(el.querySelectorAll("a")).filter((node) => {
      try {
        return range.intersectsNode(node);
      } catch (err) {
        return false;
      }
    });
    if (hitAnchors.length) {
      hitAnchors.forEach((linkEl) => {
        linkEl.style.color = hex;
        Array.from(linkEl.querySelectorAll("span, font")).forEach((span) => {
          if (!rgbToHex(span.style.color || span.getAttribute("color") || "")) return;
          while (span.firstChild) span.parentNode.insertBefore(span.firstChild, span);
          span.remove();
        });
      });
    } else {
      document.execCommand("styleWithCSS", false, true);
      document.execCommand("foreColor", false, hex);
    }
    emitFromDom();
    if (hitAnchors.length && hitAnchors.every((node) => node.isConnected)) {
      const keep = document.createRange();
      keep.selectNodeContents(hitAnchors[0]);
      if (hitAnchors.length > 1) {
        keep.setStartBefore(hitAnchors[0]);
        keep.setEndAfter(hitAnchors[hitAnchors.length - 1]);
      }
      sel.removeAllRanges();
      sel.addRange(keep);
      savedRange.current = keep.cloneRange();
      onSelectionColorRef.current(hex, true);
    }
    rememberRange();
    return true;
  }

  applyColorRef.current = applyColor;

  function applyLink(href, text) {
    const el = editorRef.current;
    const nextHref = String(href || "").trim();
    if (!el || !isHttpOrMailto(nextHref)) return false;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    let container = savedRange.current ? savedRange.current.commonAncestorContainer : null;
    if (container && container.nodeType === Node.TEXT_NODE) container = container.parentElement;
    let anchor = container && container.closest ? container.closest("a") : null;
    if (anchor && !el.contains(anchor)) anchor = null;
    if (!anchor && savedRange.current) {
      const hits = Array.from(el.querySelectorAll("a")).filter((node) => {
        try {
          return savedRange.current.intersectsNode(node);
        } catch (err) {
          return false;
        }
      });
      anchor = hits[0] || null;
    }
    if (anchor) {
      anchor.setAttribute("href", nextHref);
      if (text != null && String(text).trim() !== "") {
        const label = String(text);
        if ((anchor.innerText || "").replace(/\s+/g, " ").trim() !== label.replace(/\s+/g, " ").trim()) {
          anchor.textContent = label;
        }
      }
    } else if (savedRange.current && !savedRange.current.collapsed) {
      document.execCommand("createLink", false, nextHref);
    } else {
      return false;
    }
    emitFromDom();
    rememberRange();
    return true;
  }

  applyLinkRef.current = applyLink;

  useEffect(() => {
    function onSel() {
      rememberRange();
    }
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  return (
    <div
      ref={editorRef}
      className="edit-panel-wysiwyg"
      contentEditable
      suppressContentEditableWarning
      style={{ whiteSpace: "pre-wrap", ...style }}
      onInput={emitFromDom}
      onMouseUp={rememberRange}
      onKeyUp={rememberRange}
      onClick={(e) => {
        if (e.target.closest("a")) e.preventDefault();
      }}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
      }}
    />
  );
}

function StylePanel({ content, style, onChange, dirty, saving, onSave, onCancel }) {
  const [selectionHex, setSelectionHex] = useState("");
  const [hasRange, setHasRange] = useState(false);
  const [linkHref, setLinkHref] = useState("");
  const [linkText, setLinkText] = useState("");
  const [inLink, setInLink] = useState(false);
  const applyColorRef = useRef(null);
  const applyLinkRef = useRef(null);

  const blockHex = toHexColor(style.color);
  const colorValue = hasRange && selectionHex ? selectionHex : blockHex || selectionHex || "";
  const colorHex = toHexColor(colorValue) || "#000000";

  return (
    <div className="edit-panel" onClick={(e) => e.stopPropagation()}>
      <WysiwygEditor
        value={content}
        style={style}
        onChange={(markup) => onChange(markup, style)}
        onSelectionColor={(hex, ranged) => {
          setSelectionHex(hex || "");
          setHasRange(Boolean(ranged));
        }}
        onActiveLink={(info) => {
          setInLink(Boolean(info));
          if (info) {
            setLinkHref(info.href || "");
            setLinkText(info.text || "");
          }
        }}
        applyColorRef={applyColorRef}
        applyLinkRef={applyLinkRef}
      />
      <p className="edit-panel-hint">
        在蓝色框里点一下带下划线的链接，即可改「链接文字」和「网址」。选中普通文字后填网址，点「设为链接」。
      </p>
      <div className="edit-link-fields">
        <label>
          链接文字
          <input
            type="text"
            value={linkText}
            placeholder="显示在页面上的文字"
            onMouseDown={(e) => e.preventDefault()}
            onChange={(e) => setLinkText(e.target.value)}
          />
        </label>
        <label>
          网址
          <input
            type="text"
            value={linkHref}
            placeholder="https:// 或 mailto:邮箱"
            onMouseDown={(e) => e.preventDefault()}
            onChange={(e) => setLinkHref(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="edit-link-apply"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (applyLinkRef.current) applyLinkRef.current(linkHref, linkText);
          }}
        >
          {inLink ? "更新链接" : "设为链接"}
        </button>
      </div>
      <div className="edit-panel-grid">
        {STYLE_FIELDS.map(({ key, label, kind, options, placeholder }) => {
          const value = toCssString(style[key]);
          const update = (next) => {
            const nextStyle = { ...style };
            if (next === "") {
              delete nextStyle[key];
            } else {
              nextStyle[key] = next;
            }
            onChange(content, nextStyle);
          };
          const selectOptions =
            kind === "select" && value && !options.includes(value)
              ? [...options, value]
              : options;
          if (kind === "color") {
            return (
              <label key={key}>
                {label}
                <span className="edit-color-row">
                  <input
                    type="color"
                    value={colorHex}
                    onMouseDown={(e) => e.preventDefault()}
                    onChange={(e) => {
                      const hex = e.target.value;
                      const applied = hasRange && applyColorRef.current && applyColorRef.current(hex);
                      if (applied) {
                        return;
                      }
                      update(hex);
                    }}
                  />
                  <input
                    type="text"
                    value={colorValue}
                    placeholder="#000000"
                    onChange={(e) => {
                      const next = e.target.value;
                      const hex = toHexColor(next);
                      if (hasRange && hex && applyColorRef.current && applyColorRef.current(hex)) {
                        return;
                      }
                      update(next);
                    }}
                  />
                </span>
              </label>
            );
          }
          return (
            <label key={key}>
              {label}
              {kind === "select" ? (
                <select value={value} onChange={(e) => update(e.target.value)}>
                  {selectOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "" ? "默认" : opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={value}
                  placeholder={placeholder || ""}
                  onChange={(e) => update(e.target.value)}
                />
              )}
            </label>
          );
        })}
      </div>
      {dirty && (
        <div className="edit-panel-actions">
          <button type="button" className="primary" onClick={onSave} disabled={saving}>
            {saving ? "保存中…" : "保存"}
          </button>
          <button type="button" onClick={onCancel} disabled={saving}>
            取消
          </button>
        </div>
      )}
    </div>
  );
}

function EditableText({ id, as: Tag = "p", defaultContent, defaultStyle = {}, className }) {
  const { isPreview, slots, savePage } = usePreviewMode();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const blockRef = useRef(null);

  const slot = slots[id] && slots[id].type === "text" ? slots[id] : null;
  const committedContent = slot ? slot.content : defaultContent;
  const overrideStyle = slot && slot.style ? slot.style : {};
  const committedStyle = { ...defaultStyle, ...overrideStyle };
  const content = editing && draft ? draft.content : committedContent;
  const effectiveStyle = editing && draft ? draft.style : committedStyle;
  const dirty =
    editing &&
    draft &&
    (draft.content !== committedContent || !stylesEqual(draft.style, committedStyle));

  useEffect(() => {
    if (!editing || dirty) return undefined;
    function onPointerDown(event) {
      if (blockRef.current && !blockRef.current.contains(event.target)) {
        setEditing(false);
        setDraft(null);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [editing, dirty]);

  const rendered = (
    <Tag className={className} style={{ whiteSpace: "pre-line", ...effectiveStyle }}>
      {renderRichText(content)}
    </Tag>
  );

  if (!isPreview) {
    return rendered;
  }

  function startEditing() {
    if (editing) return;
    setDraft({ content: committedContent, style: { ...committedStyle } });
    setEditing(true);
  }

  function handleChange(nextContent, nextStyle) {
    setDraft({ content: nextContent, style: nextStyle });
  }

  async function handleSave() {
    if (!savePage || !draft) return;
    setSaving(true);
    const nextSlots = {
      ...slots,
      [id]: { type: "text", content: draft.content, style: draft.style },
    };
    const ok = await savePage(nextSlots);
    setSaving(false);
    if (ok) {
      setEditing(false);
      setDraft(null);
    }
  }

  function handleCancel() {
    setDraft(null);
    setEditing(false);
  }

  return (
    <div
      ref={blockRef}
      className={`edit-block${editing ? " is-editing" : ""}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        startEditing();
      }}
    >
      {rendered}
      {editing && draft && (
        <StylePanel
          content={draft.content}
          style={draft.style}
          onChange={handleChange}
          dirty={dirty}
          saving={saving}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}

EditableText.propTypes = {
  id: PropTypes.string.isRequired,
  as: PropTypes.string,
  defaultContent: PropTypes.string.isRequired,
  defaultStyle: PropTypes.object,
  className: PropTypes.string,
};

export default EditableText;
